import {
  TAX_REGIMES,
  UF_LIST,
  companyDataSchema,
  formatCnpj,
  type Company,
} from "@cafegestor/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { useCan } from "@/app/me";
import { Alert, Button, Card, Field, Input, PageTitle, Select, Spinner } from "@/components/ui";
import { apiFetch, errorMessage } from "@/lib/api";

type Form = Record<string, string>;

const FIELDS: { name: string; label: string; wide?: boolean }[] = [
  { name: "legalName", label: "Razão social", wide: true },
  { name: "tradeName", label: "Nome fantasia", wide: true },
  { name: "stateRegistration", label: "Inscrição estadual" },
  { name: "municipalRegistration", label: "Inscrição municipal" },
  { name: "cep", label: "CEP" },
  { name: "street", label: "Logradouro", wide: true },
  { name: "number", label: "Número" },
  { name: "complement", label: "Complemento" },
  { name: "district", label: "Bairro" },
  { name: "city", label: "Cidade" },
  { name: "cityIbgeCode", label: "Código IBGE do município" },
  { name: "phone", label: "Telefone" },
  { name: "email", label: "E-mail" },
];

const REGIME_LABEL: Record<string, string> = {
  simples_nacional: "Simples Nacional",
  mei: "MEI",
  lucro_presumido: "Lucro Presumido",
  lucro_real: "Lucro Real",
};

export function CompanyPage() {
  const can = useCan();
  const editable = can("admin.empresa.editar");
  const queryClient = useQueryClient();
  const { data, isPending, error } = useQuery({
    queryKey: ["empresa"],
    queryFn: () => apiFetch<Company>("/empresa"),
  });
  const [form, setForm] = useState<Form>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    const f: Form = {};
    for (const [k, v] of Object.entries(data))
      f[k] = v === null || v === undefined ? "" : String(v);
    setForm(f);
  }, [data]);

  const save = useMutation({
    mutationFn: (body: unknown) => apiFetch<Company>("/empresa", { method: "PATCH", body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["empresa"] }),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = companyDataSchema.safeParse({
      ...form,
      taxRegime: form["taxRegime"] || null,
      crt: form["crt"] || null,
      uf: form["uf"] || null,
    });
    if (!parsed.success) return setFormError(parsed.error.issues[0]?.message ?? "Dados inválidos");
    setFormError(null);
    save.mutate(parsed.data);
  }

  if (isPending) return <Spinner />;
  if (error) return <Alert tone="error">{errorMessage(error)}</Alert>;

  const set = (name: string) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [name]: e.target.value }));

  return (
    <div>
      <PageTitle
        title="Dados da empresa"
        description={`CNPJ ${formatCnpj(data?.cnpj ?? "")} (não editável)`}
      />
      <form onSubmit={submit}>
        <Card>
          <fieldset disabled={!editable || save.isPending} className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map((f) => (
              <div key={f.name} className={f.wide ? "sm:col-span-2" : ""}>
                <Field label={f.label}>
                  <Input value={form[f.name] ?? ""} onChange={set(f.name)} />
                </Field>
              </div>
            ))}
            <Field label="UF">
              <Select value={form["uf"] ?? ""} onChange={set("uf")}>
                <option value="">—</option>
                {UF_LIST.map((uf) => (
                  <option key={uf}>{uf}</option>
                ))}
              </Select>
            </Field>
            <Field label="Regime tributário">
              <Select value={form["taxRegime"] ?? ""} onChange={set("taxRegime")}>
                <option value="">—</option>
                {TAX_REGIMES.map((r) => (
                  <option key={r} value={r}>
                    {REGIME_LABEL[r]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="CRT">
              <Select value={form["crt"] ?? ""} onChange={set("crt")}>
                <option value="">—</option>
                <option value="1">1 — Simples Nacional</option>
                <option value="2">2 — Simples Nacional (excesso de sublimite)</option>
                <option value="3">3 — Regime Normal</option>
                <option value="4">4 — MEI</option>
              </Select>
            </Field>
          </fieldset>
          <div className="mt-5 space-y-3">
            {formError && <Alert tone="error">{formError}</Alert>}
            {save.isError && <Alert tone="error">{errorMessage(save.error)}</Alert>}
            {save.isSuccess && <Alert tone="ok">Dados salvos.</Alert>}
            {editable ? (
              <Button type="submit" disabled={save.isPending}>
                Salvar
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">
                Você pode apenas consultar estes dados.
              </p>
            )}
          </div>
        </Card>
      </form>
    </div>
  );
}
